import * as React from "react";
import { Collapse, Select, Input, Row, Col, DatePicker, InputNumber, Switch, Form } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ProjectTabProps } from "../Projects";
import { API_BILLINGINFORMATION_BILLINGMODE } from "General/APIEnums";
import { dayjs } from "Config/dayjs";
import { BillingContactsSelection } from "./BillingContactsSelection";
import { Loader } from "Components/General/Loader";

interface BillingTabProps extends ProjectTabProps, WithTranslation {
  billingInformation?: API_BillingInformation;
  status: API_ADMINPROJECT_STATUS;
}

export const BillingTab = withTranslation(I18N_NS.ADMIN)(
  class extends React.Component<BillingTabProps> {
    private modeValues: { [key: string]: string | API_BILLINGINFORMATION_BILLINGMODE }[] = [
      { name: "Storage and user", value: API_BILLINGINFORMATION_BILLINGMODE.STORAGE_AND_USER },
      { name: "Free", value: API_BILLINGINFORMATION_BILLINGMODE.FREE },
      { name: "Storage in GB", value: API_BILLINGINFORMATION_BILLINGMODE.STORAGE_IN_GB },
      { name: "Storage in document pages", value: API_BILLINGINFORMATION_BILLINGMODE.STORAGE_IN_PAGES },
    ];

    private API_PROJECTS_STATUS = {
      ENABLED: "Enabled",
      DEACTIVATED: "Deactivated",
      ARCHIVED: "Archived",
    };

    render() {
      if (!this.props.billingInformation) {
        return <Loader />;
      }

      const { t, billingInformation } = this.props;

      return (
        <div>
          <Collapse
            bordered={false}
            className="mb-2 billing-tab"
            items={[
              {
                forceRender: true,
                label: t("Basics"),
                key: "1",
                children: (
                  <Row gutter={20}>
                    <Col xs={24} md={8} lg={3}>
                      <Form.Item
                        label={t("Topix project number")}
                        name={["billingInformation", "topixProjectNumber"]}
                        initialValue={billingInformation.topixProjectNumber}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={{ span: 3, offset: 1 }}>
                      <Form.Item label={t("Status")}>{t(this.API_PROJECTS_STATUS[this.props.status])}</Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={6}>
                      <Form.Item
                        label={t("Customer (for billing)")}
                        name={["billingInformation", "invoiceRecipient"]}
                        initialValue={billingInformation.invoiceRecipient}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                forceRender: true,
                label: t("Billing mode"),
                key: "2",
                children: (
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={t("Mode")}
                        name={["billingInformation", "billingMode"]}
                        initialValue={billingInformation.billingMode}
                      >
                        <Select allowClear={true} showSearch={true} optionFilterProp="children">
                          {this.modeValues.map((mode) => {
                            return (
                              <Select.Option key={mode.value} value={mode.value}>
                                {t(mode.name)}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={t("Note")}
                        name={["billingInformation", "billingModeNote"]}
                        initialValue={billingInformation.billingModeNote}
                      >
                        <Input.TextArea rows={4} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                forceRender: true,
                label: t("Billing dates"),
                key: "3",
                children: (
                  <Row gutter={20} justify="start">
                    <Col xs={24} md={12} lg={5}>
                      <Form.Item
                        label={t("Start")}
                        name={["billingInformation", "billingStart"]}
                        initialValue={billingInformation.billingStart ? dayjs(billingInformation.billingStart) : null}
                      >
                        <DatePicker format="l" placeholder={t("Start date")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} lg={5}>
                      <Form.Item
                        label={t("End")}
                        name={["billingInformation", "billingEnd"]}
                        initialValue={billingInformation.billingEnd ? dayjs(billingInformation.billingEnd) : null}
                      >
                        <DatePicker format="l" placeholder={t("End date")} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                forceRender: true,
                label: t("Contract dates"),
                key: "4",
                children: (
                  <Row gutter={20} justify="start">
                    <Col xs={24} md={12} lg={5}>
                      <Form.Item
                        label={t("Start")}
                        name={["billingInformation", "contractStart"]}
                        initialValue={billingInformation.contractStart ? dayjs(billingInformation.contractStart) : null}
                      >
                        <DatePicker format="l" placeholder={t("Start date")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} lg={5}>
                      <Form.Item
                        label={t("End")}
                        name={["billingInformation", "contractEnd"]}
                        initialValue={billingInformation.contractEnd ? dayjs(billingInformation.contractEnd) : null}
                      >
                        <DatePicker format="l" placeholder={t("End date")} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                forceRender: true,
                label: t("Customer"),
                key: "5",
                children: (
                  <Row gutter={20}>
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label={t("Topix number customer")}
                        name={["billingInformation", "topixClientNumber"]}
                        initialValue={billingInformation.topixClientNumber}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label={t("Customer (company)")}
                        name={["billingInformation", "companyName"]}
                        initialValue={billingInformation.companyName}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label={t("Topix number superior customer")}
                        name={["billingInformation", "topixParentCompanyNumber"]}
                        initialValue={billingInformation.topixParentCompanyNumber}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} lg={6}>
                      <Form.Item
                        label={t("Superior customer (company)")}
                        name={["billingInformation", "parentCompanyName"]}
                        initialValue={billingInformation.parentCompanyName}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                forceRender: true,
                label: t("Alternative billing address"),
                key: "6",
                children: (
                  <Row gutter={20}>
                    <Col xs={24} md={6}>
                      <Form.Item
                        label={t("Name")}
                        name={["billingInformation", "billingAdressName"]}
                        initialValue={billingInformation.billingAdressName}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item
                        label={t("Street")}
                        name={["billingInformation", "billingAdressStreet"]}
                        initialValue={billingInformation.billingAdressStreet}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item
                        label={t("Post code")}
                        name={["billingInformation", "billingAdressPostalCode"]}
                        initialValue={billingInformation.billingAdressPostalCode}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item
                        label={t("City")}
                        name={["billingInformation", "billingAdressCity"]}
                        initialValue={billingInformation.billingAdressCity}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                forceRender: true,
                label: t("Contact persons"),
                key: "7",
                children: (
                  <Form.Item name={["billingInformation", "contacts"]} initialValue={billingInformation.contacts}>
                    <BillingContactsSelection />
                  </Form.Item>
                ),
              },
              {
                forceRender: true,
                label: t("Inclusive services"),
                key: "8",
                children: (
                  <Row gutter={20}>
                    <Col xs={24} md={8} lg={5}>
                      <Form.Item
                        label={t("Storage in GB")}
                        name={["billingInformation", "storageGb"]}
                        initialValue={billingInformation.storageGb}
                      >
                        <InputNumber style={{ width: "100%" }} step={0.1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={5}>
                      <Form.Item
                        label={t("User")}
                        name={["billingInformation", "users"]}
                        initialValue={billingInformation.users}
                      >
                        <InputNumber style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={5}>
                      <Form.Item
                        label={t("Storage in document pages")}
                        name={["billingInformation", "pages"]}
                        initialValue={billingInformation.pages}
                      >
                        <InputNumber style={{ width: "100%" }} step={1} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                forceRender: true,
                label: t("Additional Fees"),
                key: "9",
                children: (
                  <Row gutter={20}>
                    <Col xs={24} md={8} lg={5}>
                      <Form.Item
                        label={t("Additional € per GB")}
                        name={["billingInformation", "pricePerAdditionalGb"]}
                        initialValue={billingInformation.pricePerAdditionalGb}
                      >
                        <InputNumber style={{ width: "100%" }} step={0.1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={5}>
                      <Form.Item
                        label={t("Additional € per user")}
                        name={["billingInformation", "pricePerAdditionalUser"]}
                        initialValue={billingInformation.pricePerAdditionalUser}
                      >
                        <InputNumber style={{ width: "100%" }} step={0.1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={5}>
                      <Form.Item
                        label={t("Additional € per document page")}
                        name={["billingInformation", "pricePerAdditionalPage"]}
                        initialValue={billingInformation.pricePerAdditionalPage}
                      >
                        <InputNumber style={{ width: "100%" }} step={0.1} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                forceRender: true,
                label: t("Support agreements"),
                key: "10",
                children: (
                  <Row gutter={20}>
                    <Col xs={24}>
                      <Form.Item
                        label={t("Description")}
                        name={["billingInformation", "supportDescription"]}
                        initialValue={billingInformation.supportDescription}
                      >
                        <Input.TextArea />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                forceRender: true,
                label: t("External licences or tools"),
                key: "11",
                children: (
                  <Row gutter={20}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label={t("Online-Bauabnahme package")}
                        name={["billingInformation", "packageBuildingInspection"]}
                        initialValue={billingInformation.packageBuildingInspection}
                      >
                        <Input.TextArea />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label={t("Baudokumentation package")}
                        name={["billingInformation", "packageBuildingDocumentation"]}
                        initialValue={billingInformation.packageBuildingDocumentation}
                      >
                        <Input.TextArea />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label={t("Outlook Plugin")}
                        name={["billingInformation", "outlookPlugin"]}
                        initialValue={billingInformation.outlookPlugin}
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
            ]}
          />
        </div>
      );
    }
  }
);
